/**
 * Módulo 17 (0103) na tela — o vocabulário, as contas e as buscas da
 * telemetria, sem React. A mesa e o celular leem daqui: o rótulo de cada campo,
 * a situação do sinal e da fila, o valor com a unidade e o retorno de cada
 * gravação dizem a mesma coisa nos dois.
 *
 * NÃO HÁ TELEMETRIA REAL AINDA (docs/product/README.md, "Os ciclos 3 e 4 antes
 * do piloto"): sem mensagem recebida, a tela diz que não há — nunca zero.
 */
import { formatarDataHora, formatarDecimal, formatarDia, formatarInteiro } from '@/lib/formato';
import type { RetornoDeclarado } from '@/lib/retorno';

export const TIPOS_DE_MENSAGEM = [
  'horimetro',
  'odometro',
  'posicao',
  'motor',
  'horas_ociosas',
  'combustivel_consumido',
  'combustivel_nivel',
  'temperatura',
  'falha',
] as const;
export type TipoDeMensagem = (typeof TIPOS_DE_MENSAGEM)[number];

export const ROTULO_DO_TIPO: Readonly<Record<TipoDeMensagem, string>> = {
  horimetro: 'Horímetro',
  odometro: 'Odômetro',
  posicao: 'Posição',
  motor: 'Motor ligado',
  horas_ociosas: 'Horas ociosas',
  combustivel_consumido: 'Combustível consumido',
  combustivel_nivel: 'Nível de combustível',
  temperatura: 'Temperatura',
  falha: 'Código de falha',
};

export const UNIDADE_DO_TIPO: Readonly<Partial<Record<TipoDeMensagem, string>>> = {
  horimetro: 'h',
  horas_ociosas: 'h',
  odometro: 'km',
  combustivel_consumido: 'L',
  combustivel_nivel: '%',
  temperatura: '°C',
};

export const rotuloDoTipo = (tipo: string | null): string =>
  tipo !== null && tipo in ROTULO_DO_TIPO ? ROTULO_DO_TIPO[tipo as TipoDeMensagem] : (tipo ?? 'Sem tipo');

/* --------------------------------------------------------------- o sinal */

export type SituacaoDoSinal = 'com_sinal' | 'sem_sinal' | 'sem_vinculo' | 'desativado';
export type TomDoSelo = 'sucesso' | 'atencao' | 'erro' | 'neutro' | 'informacao';

/**
 * A situação do DISPOSITIVO, e não da máquina: "Sem sinal" não é "Parada"
 * (v6, ferramenta 4: "evita tratar ausência de sinal como máquina parada").
 * Por isso nenhum destes usa a grafia nem a cor dos seis status da máquina.
 */
export const SITUACAO_DO_SINAL: Readonly<Record<SituacaoDoSinal, { readonly rotulo: string; readonly tom: TomDoSelo }>> = {
  com_sinal: { rotulo: 'Com sinal', tom: 'sucesso' },
  sem_sinal: { rotulo: 'Sem sinal', tom: 'atencao' },
  sem_vinculo: { rotulo: 'Sem máquina', tom: 'neutro' },
  desativado: { rotulo: 'Desativado', tom: 'neutro' },
};

export interface Posicao {
  readonly latitude: number;
  readonly longitude: number;
  readonly em: string | null;
}

export interface DispositivoNoSinal {
  readonly dispositivoId: string;
  readonly identificador: string;
  readonly descricao: string | null;
  readonly integracaoId: string;
  readonly integracao: string;
  readonly frequenciaMin: number;
  readonly vinculoId: string | null;
  readonly equipamentoId: string | null;
  readonly equipamento: string | null;
  readonly maquina: string | null;
  readonly desde: string | null;
  readonly conferidoEm: string | null;
  readonly ultimoSinalEm: string | null;
  readonly ultimaLeituraEm: string | null;
  readonly situacao: SituacaoDoSinal;
  readonly limiteMin: number;
  /** O medidor da máquina do vínculo: o caminhão lê odômetro. */
  readonly medidor: 'horimetro' | 'odometro';
  readonly horimetro: number | null;
  readonly horimetroEm: string | null;
  readonly posicao: Posicao | null;
}

export interface RespostaDoSinal {
  readonly indicadores: { readonly ligados: number; readonly semSinal: number; readonly naFila: number; readonly falhas24h: number };
  readonly dispositivos: readonly DispositivoNoSinal[];
}

export interface UltimoValor {
  readonly tipo: string;
  readonly valor: number | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly lidaEm: string;
  readonly qualidade?: string;
  readonly codigo?: string | null;
}

export interface VinculoDoDispositivo {
  readonly id: string;
  readonly equipamento: string;
  readonly desde: string;
  readonly ate: string | null;
  readonly conferidoEm: string | null;
  readonly diferenca: number | null;
  readonly leituraDoPainel: number | null;
  readonly leituraDoDispositivo: number | null;
}

export interface DetalheDoDispositivo {
  readonly dispositivo: {
    readonly dispositivoId: string;
    readonly identificador: string;
    readonly integracao: string;
    readonly situacao: SituacaoDoSinal;
    readonly ultimoSinalEm: string | null;
    readonly limiteMin: number;
    readonly equipamento: string | null;
  };
  readonly ultimos: readonly UltimoValor[];
  readonly falhas: readonly { readonly codigo: string | null; readonly severidade: string | null; readonly descricao: string | null; readonly lidaEm: string }[];
  readonly vinculos: readonly VinculoDoDispositivo[];
}

/** "07/10/2026 · 14:32", ou o que falta. */
export function quando(instante: string | null | undefined, semValor = 'Nunca'): string {
  return instante ? formatarDataHora(new Date(instante)) : semValor;
}

/** O valor de um campo, com a unidade do sistema: "4.832,5 h", "Ligado", "-19,919100, -43,938600". */
export function valorEscrito(tipo: string | null, v: { readonly valor: number | null; readonly latitude?: number | null; readonly longitude?: number | null; readonly codigo?: string | null }): string {
  if (tipo === 'posicao') {
    if (v.latitude === null || v.latitude === undefined || v.longitude === null || v.longitude === undefined) return '—';
    return `${v.latitude.toFixed(6).replace('.', ',')}, ${v.longitude.toFixed(6).replace('.', ',')}`;
  }
  if (tipo === 'motor') return v.valor === null ? '—' : v.valor >= 1 ? 'Ligado' : 'Desligado';
  if (tipo === 'falha') return v.codigo ?? '—';
  if (v.valor === null) return '—';
  const unidade = tipo !== null && tipo in UNIDADE_DO_TIPO ? UNIDADE_DO_TIPO[tipo as TipoDeMensagem] : '';
  return `${formatarDecimal(v.valor, 1)}${unidade ? ` ${unidade}` : ''}`;
}

/** A frase do sem sinal, com a regra: "Mais de 45 min sem mensagem". */
export function textoDoLimite(limiteMin: number): string {
  return limiteMin >= 120 && limiteMin % 60 === 0
    ? `Avisa depois de ${formatarInteiro(limiteMin / 60)} h sem mensagem`
    : `Avisa depois de ${formatarInteiro(limiteMin)} min sem mensagem`;
}

/* ---------------------------------------------------------------- a fila */

export const FILTROS_DA_FILA = ['todas', 'pendente', 'recusada', 'suspeita', 'promocao'] as const;
export type FiltroDaFila = (typeof FILTROS_DA_FILA)[number];

export const ROTULO_DO_FILTRO: Readonly<Record<FiltroDaFila, string>> = {
  todas: 'Toda a fila',
  pendente: 'Pendentes',
  recusada: 'Recusadas',
  suspeita: 'Suspeitas',
  promocao: 'Não entraram na máquina',
};

export interface MensagemDaFila {
  readonly id: string;
  readonly idExterno: string;
  readonly identificador: string;
  readonly tipo: string | null;
  readonly lidaEm: string | null;
  readonly valor: number | null;
  readonly unidadeRecebida: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly codigo: string | null;
  readonly severidade: string | null;
  readonly situacao: 'aceita' | 'pendente' | 'recusada' | 'descartada';
  readonly qualidade: 'boa' | 'suspeita';
  readonly motivo: string | null;
  readonly promocaoRecusada: string | null;
  readonly recebidaEm: string;
  readonly equipamento: string | null;
  readonly integracao: string;
  readonly bruto: unknown;
}

export interface LoteRecebido {
  readonly id: string;
  readonly recebidoEm: string;
  readonly formato: string;
  readonly mensagens: number;
  readonly aceitas: number;
  readonly pendentes: number;
  readonly recusadas: number;
  readonly duplicadas: number;
  readonly duracaoMs: number | null;
  readonly integracao: string;
  readonly prefixo: string;
}

export interface RespostaDaFila {
  readonly mensagens: readonly MensagemDaFila[];
  readonly contagem: Readonly<Record<FiltroDaFila, number>>;
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly lotes: readonly LoteRecebido[];
}

/** Em que situação da fila a mensagem está — a palavra e o tom do selo. */
export function situacaoNaFila(m: Pick<MensagemDaFila, 'situacao' | 'qualidade' | 'promocaoRecusada'>): { readonly rotulo: string; readonly tom: TomDoSelo } {
  if (m.situacao === 'recusada') return { rotulo: 'Recusada', tom: 'erro' };
  if (m.situacao === 'pendente') return { rotulo: 'Pendente', tom: 'atencao' };
  if (m.situacao === 'descartada') return { rotulo: 'Descartada', tom: 'neutro' };
  if (m.promocaoRecusada !== null) return { rotulo: 'Não entrou na máquina', tom: 'atencao' };
  if (m.qualidade === 'suspeita') return { rotulo: 'Suspeita', tom: 'atencao' };
  return { rotulo: 'Aceita', tom: 'sucesso' };
}

/** O que fazer com ela: a frase do servidor (a promoção recusada primeiro). */
export const oQueFazer = (m: Pick<MensagemDaFila, 'motivo' | 'promocaoRecusada'>): string =>
  m.promocaoRecusada ?? m.motivo ?? 'Sem pendência.';

/** Reprocessar serve à pendente e à que o dia não aceitou; a recusada volta corrigida pelo provedor. */
export const podeReprocessar = (m: Pick<MensagemDaFila, 'situacao' | 'promocaoRecusada'>): boolean =>
  m.situacao === 'pendente' || (m.situacao === 'aceita' && m.promocaoRecusada !== null);

export interface BuscaDaFila {
  readonly situacao?: FiltroDaFila;
  readonly pagina?: number;
}

/** A URL é entrada do usuário: o que não está no vocabulário é descartado, e não corrigido no chute. */
export function validarBuscaDaFila(entrada: Record<string, unknown>): BuscaDaFila {
  const situacao = entrada['situacao'];
  const pagina = Number(entrada['pagina']);
  return {
    ...(typeof situacao === 'string' && (FILTROS_DA_FILA as readonly string[]).includes(situacao) && situacao !== 'todas'
      ? { situacao: situacao as FiltroDaFila }
      : {}),
    ...(Number.isInteger(pagina) && pagina > 1 && pagina <= 10_000 ? { pagina } : {}),
  };
}

/* ---------------------------------------------------------- a conciliação */

export interface DiaConciliado {
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly dia: string;
  readonly horasMotorTelemetria: number | null;
  readonly horasOciosas: number | null;
  readonly km: number | null;
  readonly horasMotorApontadas: number | null;
  readonly horasTurno: number | null;
  readonly apontamentos: number | null;
  readonly diferenca: number | null;
  readonly acimaDaTolerancia: boolean | null;
  readonly divergencia: string | null;
}

export interface ConsumoConciliado {
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly litrosTelemetria: number;
  readonly diasComDado: number;
  readonly litrosAbastecidos: number | null;
  readonly diferenca: number | null;
  readonly capacidadeL: number | null;
  readonly acimaDaTolerancia: boolean | null;
}

export interface RespostaDaConciliacao {
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly premissas: { readonly toleranciaH: number; readonly toleranciaConsumoPct: number; readonly horas: string; readonly consumo: string };
  readonly leOperacao: boolean;
  readonly leCombustivel: boolean;
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly dias: readonly DiaConciliado[];
  readonly consumo: readonly ConsumoConciliado[];
}

export interface BuscaDaConciliacaoDaTelemetria {
  readonly de?: string;
  readonly ate?: string;
  readonly pagina?: number;
}

const DIA = /^\d{4}-\d{2}-\d{2}$/;

export function validarBuscaDaConciliacaoDaTelemetria(entrada: Record<string, unknown>): BuscaDaConciliacaoDaTelemetria {
  const de = entrada['de'];
  const ate = entrada['ate'];
  const pagina = Number(entrada['pagina']);
  return {
    ...(typeof de === 'string' && DIA.test(de) ? { de } : {}),
    ...(typeof ate === 'string' && DIA.test(ate) ? { ate } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 && pagina <= 10_000 ? { pagina } : {}),
  };
}

/** "Em aberto", "Resolvida" ou "Não apurada": a divergência do dia na fila da conciliação de horas. */
export function divergenciaEscrita(d: Pick<DiaConciliado, 'divergencia' | 'acimaDaTolerancia'>): string {
  if (d.divergencia === 'aberta') return 'Aberta na conciliação';
  if (d.divergencia === 'resolvida') return 'Resolvida';
  return d.acimaDaTolerancia ? 'Ainda não apurada' : '—';
}

/** "05/10/2026 a 11/10/2026" — ou um dia só. */
export function periodoEscrito(de: string, ate: string): string {
  return de === ate ? formatarDia(de) : `${formatarDia(de)} a ${formatarDia(ate)}`;
}

/* ------------------------------------------- as integrações e o cadastro */

export const FORMATOS = { padrao: 'Formato padrão (JSON)', aemp: 'AEMP 2.0 (ISO 15143-3)' } as const;
export type Formato = keyof typeof FORMATOS;

export interface Integracao {
  readonly id: string;
  readonly nome: string;
  readonly formato: Formato;
  readonly campos: readonly TipoDeMensagem[];
  readonly frequenciaMin: number;
  readonly licencaAte: string | null;
  readonly observacao: string | null;
  readonly ativa: boolean;
  readonly criadoEm: string;
  readonly dispositivos: number;
  readonly ultimoLoteEm: string | null;
}

export interface ChaveDaIntegracao {
  readonly id: string;
  readonly integracaoId: string;
  readonly prefixo: string;
  readonly criadaEm: string;
  readonly criadaPor: string | null;
  readonly revogadaEm: string | null;
  readonly revogadaPor: string | null;
  readonly motivoRevogacao: string | null;
  readonly ultimoUsoEm: string | null;
}

export interface DispositivoCadastrado {
  readonly id: string;
  readonly integracaoId: string;
  readonly integracao: string;
  readonly identificador: string;
  readonly descricao: string | null;
  readonly ativo: boolean;
  readonly vinculoId: string | null;
  readonly equipamentoId: string | null;
  readonly equipamento: string | null;
  readonly desde: string | null;
  readonly conferidoEm: string | null;
  readonly leituraDoPainel: number | null;
  readonly leituraDoDispositivo: number | null;
  readonly diferenca: number | null;
  readonly medidor: 'horimetro' | 'odometro';
  readonly ultimoContador: number | null;
}

export interface Regras {
  readonly semSinalVezes: number;
  readonly ociosidadePct: number;
  readonly toleranciaConsumoPct: number;
  readonly padrao: boolean;
}

export interface RespostaDasIntegracoes {
  readonly integracoes: readonly Integracao[];
  readonly chaves: readonly ChaveDaIntegracao[];
  readonly dispositivos: readonly DispositivoCadastrado[];
  readonly totalDeDispositivos: number;
  readonly regras: Regras;
}

export interface DadosDaIntegracao {
  readonly nome: string;
  readonly formato: Formato;
  readonly campos: readonly TipoDeMensagem[];
  readonly frequenciaMin: string;
  readonly licencaAte: string;
}

export const INTEGRACAO_VAZIA: DadosDaIntegracao = { nome: '', formato: 'padrao', campos: ['horimetro', 'posicao'], frequenciaMin: '15', licencaAte: '' };

export function faltaNaIntegracao(d: DadosDaIntegracao): Partial<Record<'nome' | 'campos' | 'frequenciaMin', string>> {
  const falta: Partial<Record<'nome' | 'campos' | 'frequenciaMin', string>> = {};
  if (!d.nome.trim()) falta.nome = 'Informe o nome da integração, como o provedor a chama.';
  if (d.campos.length === 0) falta.campos = 'Marque ao menos um campo que o dispositivo manda.';
  const f = Number(d.frequenciaMin);
  if (!Number.isInteger(f) || f < 1 || f > 1440) falta.frequenciaMin = 'Informe a frequência em minutos, de 1 a 1.440.';
  return falta;
}

/** A medida da conferência: "Painel 1.500,0 h · dispositivo 1.011,0 h · diferença 489,0 h". */
export function conferenciaEscrita(d: Pick<DispositivoCadastrado, 'leituraDoPainel' | 'leituraDoDispositivo' | 'diferenca' | 'medidor'>): string {
  if (d.leituraDoPainel === null || d.leituraDoDispositivo === null) return 'Medidor não conferido: a telemetria ainda não alimenta a máquina';
  const u = d.medidor === 'odometro' ? 'km' : 'h';
  return `Painel ${formatarDecimal(d.leituraDoPainel, 1)} ${u} · dispositivo ${formatarDecimal(d.leituraDoDispositivo, 1)} ${u} · diferença ${formatarDecimal(d.diferenca ?? 0, 1)} ${u}`;
}

/** O endereço da entrada, para o provedor: relativo ao servidor da API. */
export const ROTA_DA_ENTRADA: Readonly<Record<Formato, string>> = { padrao: '/telemetria/mensagens', aemp: '/telemetria/aemp' };

export const EXEMPLO_DO_FORMATO = `{
  "mensagens": [
    { "id": "A1-000123", "dispositivo": "SN-4471",
      "instante": "2026-10-07T14:32:00-03:00",
      "tipo": "horimetro", "valor": 4832.5, "unidade": "h" },
    { "id": "A1-000124", "dispositivo": "SN-4471",
      "instante": "2026-10-07T14:32:00-03:00",
      "tipo": "posicao", "latitude": -19.9191, "longitude": -43.9386 }
  ]
}`;

/* ------------------------------------------------------- os retornos (R04) */

export interface RespostaDaChave {
  readonly id: string;
  readonly integracao: string;
  readonly prefixo: string;
  readonly chave: string;
}

/** O que cada retorno diz além do título (o título fica escrito na chamada: R04, o teste confere). */
type SemTitulo = Omit<RetornoDeclarado, 'titulo'>;

export const retornoDaChave = (r: RespostaDaChave): SemTitulo => ({
  alvo: { codigo: r.prefixo, nome: r.integracao },
  fatos: ['A chave aparece só agora: copie e entregue ao provedor'],
  efeito: 'O provedor manda as mensagens com ela no cabeçalho Authorization',
});

export const retornoDaRevogacao = (r: { readonly prefixo: string }, motivo: string): SemTitulo => ({
  alvo: { codigo: r.prefixo },
  fatos: [`Motivo: ${motivo.trim()}`],
  efeito: 'Nada mais entra por ela; ela não volta a valer',
});

export const retornoDoDescarte = (r: { readonly descartadas: number }, motivo: string): SemTitulo => ({
  fatos: [`${formatarInteiro(r.descartadas)} ${r.descartadas === 1 ? 'mensagem' : 'mensagens'}`, `Motivo: ${motivo.trim()}`],
  efeito: 'Saem da fila e da conta do dia; a trilha guarda quem descartou',
});

export const retornoDoReprocesso = (r: { readonly reprocessadas: number; readonly aceitas: number; readonly continuamNaFila: number }): SemTitulo => ({
  tom: r.continuamNaFila > 0 ? 'ressalva' : 'sucesso',
  fatos: [
    `${formatarInteiro(r.aceitas)} ${r.aceitas === 1 ? 'aceita' : 'aceitas'}`,
    r.continuamNaFila > 0 ? `${formatarInteiro(r.continuamNaFila)} continuam na fila` : null,
  ],
  efeito: r.continuamNaFila > 0 ? 'A frase de cada uma diz o que ainda falta' : 'Entram na conta do dia e na leitura da máquina quando o dia fechar',
});

export const retornoDaConferencia = (r: { readonly equipamento: string; readonly diferenca: number }, unidade: string): SemTitulo => ({
  alvo: { codigo: r.equipamento },
  fatos: [`Diferença painel − dispositivo: ${formatarDecimal(r.diferenca, 1)} ${unidade}`],
  efeito: 'A partir de agora, a última leitura de cada dia fechado vira leitura da máquina',
});
